import { DeviationRowWidget } from './deviation-row-widget';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { AttachmentsListWidget } from './attachments-list-widget';

export interface ReportDetails {
  id: number;
  companyName: string;
  reportYear: string;

  metrics: Record<
    string,
    {
      plan: string;
      fact: string;
    }
  >;

  comment?: string;
  projectNames?: string;
  projectReadiness?: string;
  rids?: string;
  commercialization?: string;
  stateSupport?: string;

  attachments?: {
    id: string;
    name: string;
    url: string;
    size: string;
    type?: string;
  }[];
}

interface ApiErrorResponse {
  success: false;
  msg: string;
}

interface ApiSuccessResponse<T> {
  success: true;
  data: T;
}

type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;

const getIconClass = (type: string) => {
  if (type.includes('pdf')) return 'bi-file-earmark-pdf';
  if (type.includes('image')) return 'bi-file-earmark-image';
  if (type.includes('zip')) return 'bi-file-earmark-zip';
  return 'bi-file-earmark';
};

function getFileExtension(filename: string): string | null {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop()! : null;
}

export class ReportFormWidget {
  private form = document.getElementById('reportForm') as HTMLFormElement | null;
  private rowWidgets: DeviationRowWidget[] = [];
  private filesContainer: HTMLElement | null = null;
  private attachmentsListWidget: AttachmentsListWidget | null = null;
  private editingEnabled = false;

  constructor(attachmentsListWidget?: AttachmentsListWidget, editingEnabled = false) {
    this.editingEnabled = editingEnabled;
    if (attachmentsListWidget) {
      this.attachmentsListWidget = attachmentsListWidget;
    }
  }

  public init(): void {
    // если есть id отчета, значит мы его редактируем и нужно подгрузить данные предыдущего отчета
    const reportId = this.getReportIdFromUrl();
    this.filesContainer = document.getElementById('reportAttachmentsList');
    if (!reportId) {
      this.initDeviationRows();
    } else {
      this.loadReport(reportId);
    }
  }



  private initDeviationRows(): void {
    if (!this.form) return;
    const rows = this.form.querySelectorAll<HTMLElement>('.report-row');

    rows.forEach((row) => {
      const widget = new DeviationRowWidget(row);
      widget.init();
      this.rowWidgets.push(widget);
    });
  }

  private async loadReport(id: number): Promise<void> {
    try {
      const res = await fetch(getApiUrl(`/api/getReport.php?Id=${id}`));
      const response = (await res.json()) as ApiResponse<ReportDetails>;

      if (!response.success) {
        console.error(response.msg);
        return;
      }

      this.fillForm(response.data);
      // Для отладки
      // this.fillForm(mockReportDetails);
      this.initDeviationRows();
    } catch (error) {
      console.error('Ошибка загрузки отчёта', error);
    }
  }

  private fillForm(data: ReportDetails): void {
    (document.getElementById('companyName') as HTMLInputElement).value = data.companyName;

    (document.getElementById('reportYear') as HTMLSelectElement).value = String(data.reportYear);

    const reportYearSelect = document.getElementById('reportYear') as HTMLSelectElement;
    reportYearSelect.value = String(data.reportYear);

    // Отключаем выбор отчётного периода при редактировании существующего отчёта
    if (this.getReportIdFromUrl() !== null) {
      reportYearSelect.disabled = true;
    }

    Object.entries(data.metrics).forEach(([key, values]) => {
      const row = document.querySelector(`.report-row[data-key="${key}"]`);
      if (!row) return;

      (row.querySelector('.plan-input') as HTMLInputElement).value = String(values.plan);

      (row.querySelector('.fact-input') as HTMLInputElement).value = String(values.fact);
    });

    this.fillTextarea('comment', data.comment);
    this.fillTextarea('projectNames', data.projectNames);
    this.fillTextarea('projectReadiness', data.projectReadiness);
    this.fillTextarea('rids', data.rids);
    this.fillTextarea('commercialization', data.commercialization);
    this.fillTextarea('stateSupport', data.stateSupport);

    if (data?.attachments?.length && this.filesContainer && !this.editingEnabled) {
      this.renderFiles(data?.attachments, this.filesContainer);
    }

    if (data?.attachments?.length && this.editingEnabled && this.attachmentsListWidget) {
      this.attachmentsListWidget.addFilesFromUrls(data.attachments);
    }
  }

  private fillTextarea(id: string, value?: string): void {
    if (!value || !this.form) return;
    const el = this.form.querySelector(`#${id}`) as HTMLTextAreaElement;
    if (el) el.value = value;
  }

  private renderFiles(files: ReportDetails['attachments'], container: HTMLElement): void {
    container.innerHTML = '';

    if (!files || files.length === 0) return;

    const ul = document.createElement('ul');
    ul.className = 'list-group d-flex flex-wrap ps-0 gap-3';

    files.forEach((file) => {
      const fileType = file?.type ?? getFileExtension(file.name);

      const li = document.createElement('li');
      li.className = 'list-group-item d-flex align-items-center gap-1 text-nowrap';

      const icon = document.createElement('i');
      icon.className = `bi ${getIconClass(fileType ?? '')}`;

      const link = document.createElement('a');
      link.href = file.url;
      link.textContent = `${file.name} (${file.size})`;
      link.rel = 'noopener';
      link.download = file.name;
      link.classList.add('text-decoration-none');

      li.appendChild(icon);
      li.appendChild(link);
      ul.appendChild(li);
    });

    container.appendChild(ul);
  }

  private getReportIdFromUrl(): number | null {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('Id');
    return id ? Number(id) : null;
  }
}
